(() => {
  // src/lib/filter-request.ts
  var buildFilterEntrySet = (params) => {
    const entries = /* @__PURE__ */ new Set();
    params.forEach((value, key) => {
      if (key.startsWith("filter.")) {
        entries.add(`${key}\0${value}`);
      }
    });
    return entries;
  };
  var mergeFilterLinkUrl = ({
    currentUrl,
    linkHref,
    completedUrl,
    fallbackUrl
  }) => {
    const fallback = fallbackUrl || currentUrl || linkHref;
    const completed = new URL(completedUrl || fallback, window.location.origin);
    const link = new URL(linkHref, window.location.origin);
    const result = new URL(currentUrl || fallback, window.location.origin);
    result.searchParams.delete("page");
    const completedEntries = buildFilterEntrySet(completed.searchParams);
    const linkEntries = buildFilterEntrySet(link.searchParams);
    for (const entry of linkEntries) {
      if (completedEntries.has(entry)) continue;
      const [key, value] = entry.split("\0");
      if (!result.searchParams.getAll(key).includes(value)) {
        result.searchParams.append(key, value);
      }
    }
    for (const entry of completedEntries) {
      if (linkEntries.has(entry)) continue;
      const [key, value] = entry.split("\0");
      const remaining = result.searchParams.getAll(key).filter((paramValue) => paramValue !== value);
      result.searchParams.delete(key);
      remaining.forEach(
        (paramValue) => result.searchParams.append(key, paramValue)
      );
    }
    return result.toString();
  };

  // src/filtering-search.ts
  (() => {
    const PANEL_TRANSITION_MS = 320;
    const modal = document.getElementById("search-modal");
    if (!modal) return;
    const body = modal.querySelector("[data-search-modal-body]");
    let debounceTimer;
    let currentQuery = "";
    // Tracks which resource type tab is active; synced on every tab click.
    let currentType = "product";
    let lastFetchedUrl = "";
    let filterPanelOpen = false;
    let activeFetch = null;
    let lastCompletedUrl = "";
    const checkIconTemplate = document.getElementById("filter-check-icon");
    const RESOURCE_TYPES = ["product", "collection", "page", "article"];
    // Tabs always render, in order, and stay clickable regardless of result count — only the
    // per-tab counts are progressively filled in. The active tab's own count is known immediately
    // from the server render (data-result-count); the other 3 aren't knowable server-side without
    // extra search-backend requests (the `search` object only ever reflects the type the current
    // request filtered to), so each is fetched independently via the Section Rendering API
    // (?section_id=, returns just this section's HTML) and applied as soon as it resolves, without
    // blocking the tab bar or the currently-displayed results.
    let lastCounts = { product: 0, collection: 0, page: 0, article: 0 };
    let countsFetchController = null;
    const getSectionId = () => {
      const el = document.querySelector('[id^="filtering-search-"]');
      return el ? el.id.replace(/^filtering-search-/, "") : null;
    };
    const fetchTypeResultCount = async (term, type, sectionId, signal) => {
      const url = `/search?q=${encodeURIComponent(term)}&type=${type}&section_id=${sectionId}`;
      const res = await fetch(url, { signal });
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, "text/html");
      const resultsEl = doc.querySelector("[data-search-results]");
      const count = resultsEl ? parseInt(resultsEl.dataset.resultCount || "0", 10) : 0;
      return Number.isNaN(count) ? 0 : count;
    };
    const applyTabCounts = (root, counts) => {
      const nav = root == null ? void 0 : root.querySelector("[data-search-tabs]");
      if (!nav) return;
      Object.keys(counts).forEach((type) => {
        const tab = nav.querySelector(`[data-search-tab="${type}"]`);
        const countEl = tab == null ? void 0 : tab.querySelector("[data-tab-count]");
        if (countEl) countEl.textContent = ` (${counts[type]})`;
      });
    };
    // knownType/knownCount come from whatever was just server-rendered (no fetch needed for it);
    // the other 3 types are each fetched independently so counts appear as soon as they're ready
    // instead of waiting on the slowest one.
    const updateTabCounts = (term, root, knownType, knownCount) => {
      countsFetchController == null ? void 0 : countsFetchController.abort();
      if (!term) {
        lastCounts = { product: 0, collection: 0, page: 0, article: 0 };
        const nav = root == null ? void 0 : root.querySelector("[data-search-tabs]");
        nav == null ? void 0 : nav.querySelectorAll("[data-tab-count]").forEach((el) => {
          el.textContent = "";
        });
        return;
      }
      lastCounts[knownType] = knownCount;
      applyTabCounts(root, { [knownType]: knownCount });
      const sectionId = getSectionId();
      if (!sectionId) return;
      const controller = new AbortController();
      countsFetchController = controller;
      RESOURCE_TYPES.filter((type) => type !== knownType).forEach(async (type) => {
        try {
          const count = await fetchTypeResultCount(term, type, sectionId, controller.signal);
          if (countsFetchController !== controller) return;
          lastCounts[type] = count;
          applyTabCounts(root, { [type]: count });
        } catch (err) {
          if (err.name === "AbortError") return;
        }
      });
    };
    const openModal = () => {
      var _a;
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      void modal.offsetWidth;
      modal.classList.add("is-open");
      (_a = modal.querySelector("[data-search-input]")) == null ? void 0 : _a.focus();
    };
    const closeModal = () => {
      modal.classList.remove("is-open");
      modal.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      closeFilterPanel();
      if (window.innerWidth < 768) {
        setTimeout(() => {
          modal.hidden = true;
        }, PANEL_TRANSITION_MS);
      } else {
        modal.hidden = true;
      }
    };
    const openFilterPanel = (animate = true) => {
      const panel = body == null ? void 0 : body.querySelector("#filter-panel");
      const backdrop = body == null ? void 0 : body.querySelector("#filter-panel-backdrop");
      if (!panel) return;
      panel.removeAttribute("hidden");
      backdrop == null ? void 0 : backdrop.removeAttribute("hidden");
      if (!animate) {
        panel.style.transition = "none";
        if (backdrop) backdrop.style.transition = "none";
      }
      void panel.offsetWidth;
      panel.classList.add("is-open");
      backdrop == null ? void 0 : backdrop.classList.add("is-open");
      if (!animate) {
        void panel.offsetWidth;
        panel.style.transition = "";
        if (backdrop) backdrop.style.transition = "";
      }
      panel.setAttribute("aria-hidden", "false");
      filterPanelOpen = true;
    };
    const closeFilterPanel = () => {
      const panel = body == null ? void 0 : body.querySelector("#filter-panel");
      const backdrop = body == null ? void 0 : body.querySelector("#filter-panel-backdrop");
      if (panel) {
        panel.classList.remove("is-open");
        backdrop == null ? void 0 : backdrop.classList.remove("is-open");
        panel.setAttribute("aria-hidden", "true");
        setTimeout(() => {
          panel.setAttribute("hidden", "");
          backdrop == null ? void 0 : backdrop.setAttribute("hidden", "");
        }, PANEL_TRANSITION_MS);
      }
      filterPanelOpen = false;
    };
    const syncThumbsFromFields = (container) => {
      const minField = container.querySelector("[data-price-field-min]");
      const maxField = container.querySelector("[data-price-field-max]");
      const minThumb = container.querySelector("[data-price-thumb-min]");
      const maxThumb = container.querySelector("[data-price-thumb-max]");
      if (minThumb && minField) minThumb.value = minField.value;
      if (maxThumb && maxField) maxThumb.value = maxField.value;
    };
    const syncPriceSlider = (container) => {
      const minThumb = container.querySelector("[data-price-thumb-min]");
      const maxThumb = container.querySelector("[data-price-thumb-max]");
      const track = container.querySelector("[data-price-track]");
      if (!minThumb || !maxThumb || !track) return;
      const rangeMin = parseFloat(minThumb.min);
      const rangeMax = parseFloat(minThumb.max);
      const minPct = (parseFloat(minThumb.value) - rangeMin) / (rangeMax - rangeMin) * 100;
      const maxPct = (parseFloat(maxThumb.value) - rangeMin) / (rangeMax - rangeMin) * 100;
      track.style.background = `linear-gradient(to right,
      var(--color-light-gray, #e0e0e0) ${minPct}%,
      var(--color-dark-gray) ${minPct}%,
      var(--color-dark-gray) ${maxPct}%,
      var(--color-light-gray, #e0e0e0) ${maxPct}%)`;
    };
    const applyPriceFilter = (container) => {
      const minField = container.querySelector("[data-price-field-min]");
      const maxField = container.querySelector("[data-price-field-max]");
      const minParam = container.dataset.minParam;
      const maxParam = container.dataset.maxParam;
      if (!minField || !maxField || !minParam || !maxParam) return;
      const url = new URL(lastFetchedUrl || `/search?q=${encodeURIComponent(currentQuery)}&type=${currentType}`, window.location.origin);
      url.searchParams.set(minParam, String(Math.round(parseFloat(minField.value))));
      url.searchParams.set(maxParam, String(Math.round(parseFloat(maxField.value))));
      url.searchParams.delete("page");
      fetchPage(url.toString(), false);
    };
    const fetchPage = async (url, restoreInput = false) => {
      var _a, _b, _c, _d;
      activeFetch == null ? void 0 : activeFetch.abort();
      const controller = new AbortController();
      activeFetch = controller;
      lastFetchedUrl = url;
      if (!body) return;
      try {
        const res = await fetch(url, { signal: controller.signal });
        const text = await res.text();
        if (activeFetch !== controller) return;
        const doc = new DOMParser().parseFromString(text, "text/html");
        const main = doc.getElementById("MainContent");
        if (!main) return;
        await Promise.all(
          Array.from(main.querySelectorAll('link[rel="stylesheet"]')).map((link) => {
            link.remove();
            if (document.head.querySelector(`link[href="${link.href}"]`)) return Promise.resolve();
            const el = document.createElement("link");
            el.rel = "stylesheet";
            el.href = link.href;
            return new Promise((resolve) => {
              el.onload = () => resolve();
              el.onerror = () => resolve();
              document.head.appendChild(el);
            });
          })
        );
        const liveValue = (_b = (_a = body.querySelector("[data-search-input]")) == null ? void 0 : _a.value) != null ? _b : currentQuery;
        const panelScrollTop = filterPanelOpen ? (_d = (_c = body.querySelector("#filter-panel")) == null ? void 0 : _c.scrollTop) != null ? _d : 0 : 0;
        body.innerHTML = main.innerHTML;
        body.scrollTop = 0;
        lastCompletedUrl = url;
        if (filterPanelOpen) {
          openFilterPanel(false);
          const restoredPanel = body.querySelector("#filter-panel");
          if (restoredPanel && panelScrollTop > 0) restoredPanel.scrollTop = panelScrollTop;
        }
        body.querySelectorAll("[data-price-range]").forEach(syncPriceSlider);
        // The freshly-rendered tab bar only has the active tab's count filled in server-side —
        // reapply the other 3 from cache so tab clicks, pagination, filters, and sorting don't
        // need their own count fetch (the term hasn't changed, so the other counts haven't
        // either). The active tab's own count is always taken fresh from this render in case it
        // just changed (e.g. a product filter was applied).
        const freshCount = (_a = body.querySelector("[data-search-results]")) == null ? void 0 : _a.dataset.resultCount;
        if (freshCount !== void 0) lastCounts[currentType] = parseInt(freshCount, 10) || 0;
        applyTabCounts(body, lastCounts);
        if (restoreInput) {
          const injectedInput = body.querySelector("[data-search-input]");
          if (injectedInput) {
            injectedInput.value = liveValue;
            injectedInput.focus();
            injectedInput.setSelectionRange(liveValue.length, liveValue.length);
          }
        }
      } catch (err) {
        if (err.name === "AbortError") return;
      }
    };
    const applyFilterLink = (linkHref) => {
      const fallback = `/search?q=${encodeURIComponent(currentQuery)}&type=${currentType}`;
      return mergeFilterLinkUrl({
        currentUrl: lastFetchedUrl || fallback,
        linkHref,
        completedUrl: lastCompletedUrl || fallback,
        fallbackUrl: fallback
      });
    };
    const fetchSection = async (query) => {
      currentQuery = query;
      const url = new URL(lastFetchedUrl || "/search", window.location.origin);
      url.pathname = "/search";
      url.searchParams.set("q", query);
      // Use the active tab type — preserves the selected tab when typing.
      url.searchParams.set("type", currentType);
      url.searchParams.delete("page");
      await fetchPage(url.toString(), true);
      if (currentQuery !== query) return;
      // The active tab's fresh count is already applied by fetchPage; kick off the other 3
      // (the term changed, so cached counts from the previous term are no longer valid).
      updateTabCounts(query, body, currentType, lastCounts[currentType]);
    };
    document.addEventListener("click", (e) => {
      const target = e.target;
      // Tab click — switch resource type, close filter panel, re-fetch with new type.
      const tabLink = target.closest("[data-search-tab]");
      if (tabLink && body?.contains(tabLink)) {
        e.preventDefault();
        const tabType = tabLink.dataset.searchTab;
        if (tabType && tabType !== currentType) {
          currentType = tabType;
          // Filter panel is products-only; close it when leaving the products tab.
          if (filterPanelOpen) closeFilterPanel();
          fetchPage(tabLink.href, false);
        }
        return;
      }
      if (target.closest("[data-search-open]")) {
        e.preventDefault();
        openModal();
        if (!(body == null ? void 0 : body.innerHTML.trim())) fetchSection("");
        return;
      }
      if (target.closest("[data-search-modal-close]")) {
        closeModal();
        return;
      }
      if (target.closest(".collection-toolbar__filter") && (body == null ? void 0 : body.contains(target))) {
        openFilterPanel();
        return;
      }
      if (target.closest("[data-filter-panel-close]")) {
        closeFilterPanel();
        return;
      }
      const filterLink = target.closest("[data-filter-url]");
      if (filterLink && (body == null ? void 0 : body.contains(filterLink))) {
        e.preventDefault();
        const checkbox = filterLink.querySelector(".search-filter-panel__checkbox");
        if (checkbox) {
          const nowActive = !checkbox.classList.contains("is-active");
          checkbox.classList.toggle("is-active", nowActive);
          checkbox.innerHTML = "";
          if (nowActive && checkIconTemplate) checkbox.appendChild(checkIconTemplate.content.cloneNode(true));
        }
        fetchPage(applyFilterLink(filterLink.href), false);
        return;
      }
      if (target.closest("[data-filter-clear-all]") && (body == null ? void 0 : body.contains(target))) {
        e.preventDefault();
        const fallback = `/search?q=${encodeURIComponent(currentQuery)}&type=${currentType}`;
        const url = new URL(lastFetchedUrl || fallback, window.location.origin);
        [...url.searchParams.keys()].filter((k) => k.startsWith("filter.")).forEach((k) => url.searchParams.delete(k));
        url.searchParams.delete("page");
        fetchPage(url.toString(), false);
        return;
      }
      const paginationLink = target.closest(".pagination a[href]");
      if (paginationLink && (body == null ? void 0 : body.contains(paginationLink))) {
        e.preventDefault();
        fetchPage(paginationLink.href);
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !modal.hidden) {
        if (filterPanelOpen) closeFilterPanel();
        else closeModal();
      }
    });
    document.addEventListener("input", (e) => {
      const target = e.target;
      const searchInput = target.closest("[data-search-input]");
      if (searchInput && !modal.hidden) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => fetchSection(searchInput.value), 300);
        return;
      }
      const priceThumbMin = target.closest("[data-price-thumb-min]");
      if (priceThumbMin) {
        const container = priceThumbMin.closest("[data-price-range]");
        if (!container) return;
        const field = container.querySelector("[data-price-field-min]");
        if (field) field.value = priceThumbMin.value;
        syncPriceSlider(container);
        return;
      }
      const priceThumbMax = target.closest("[data-price-thumb-max]");
      if (priceThumbMax) {
        const container = priceThumbMax.closest("[data-price-range]");
        if (!container) return;
        const field = container.querySelector("[data-price-field-max]");
        if (field) field.value = priceThumbMax.value;
        syncPriceSlider(container);
      }
    });
    document.addEventListener("change", (e) => {
      const target = e.target;
      const priceThumb = target.closest("[data-price-thumb-min], [data-price-thumb-max]");
      if (priceThumb) {
        const container = priceThumb.closest("[data-price-range]");
        if (container) applyPriceFilter(container);
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      const target = e.target;
      const priceField = target.closest("[data-price-field-min], [data-price-field-max]");
      if (priceField) {
        const container = priceField.closest("[data-price-range]");
        if (container) {
          syncThumbsFromFields(container);
          syncPriceSlider(container);
          applyPriceFilter(container);
        }
      }
    });
    document.addEventListener("submit", (e) => {
      const form = e.target.closest("form");
      if (form && modal.contains(form)) e.preventDefault();
    });
    document.addEventListener("dropdown:change", (e) => {
      if (!(body == null ? void 0 : body.contains(e.target))) return;
      e.stopImmediatePropagation();
      const sortValue = e.detail.value;
      if (!sortValue) return;
      const base = lastFetchedUrl || `/search?q=${encodeURIComponent(currentQuery)}&type=${currentType}`;
      const url = new URL(base, window.location.origin);
      url.searchParams.set("sort_by", sortValue);
      url.searchParams.delete("page");
      fetchPage(url.toString(), true);
    }, true);
    // The section can also render directly into MainContent on the standalone /search template
    // (not just fetched into the modal). That instance never runs through fetchPage, so it needs
    // its own one-time pass to kick off the other 3 tabs' counts on load.
    const initStandalonePage = () => {
      const root = document.getElementById("MainContent");
      if (!root || (body == null ? void 0 : body.contains(root))) return;
      const tabsNav = root.querySelector("[data-search-tabs]");
      const input = root.querySelector("[data-search-input]");
      const resultsEl = root.querySelector("[data-search-results]");
      if (!tabsNav || !input) return;
      const term = input.value.trim();
      const activeTab = tabsNav.querySelector("[data-search-tab].is-active");
      const activeType = (activeTab == null ? void 0 : activeTab.dataset.searchTab) || "product";
      const activeCount = resultsEl ? parseInt(resultsEl.dataset.resultCount || "0", 10) || 0 : 0;
      updateTabCounts(term, root, activeType, activeCount);
    };
    initStandalonePage();
  })();
})();
